import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { backendURL } from "../config";
import {
    Monitor, Server, Shield, Activity, AlertTriangle, CheckCircle2,
    RefreshCw, ChevronDown, Search, Filter, Wifi, WifiOff, X,
    Globe, ShieldAlert, ShieldCheck, Check, LayoutDashboard,
    MessageSquare, ChevronUp, AlertOctagon, RotateCcw
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// ═══════════════════════════════════════════════════════════════════════
// Authentic Zabbix 7.0 Severity Specifications
// ═══════════════════════════════════════════════════════════════════════

const ZABBIX_SEVERITIES = [
    { key: 'disaster', label: 'Disaster', short: 'DIS', bg: '#E45959', text: '#FFFFFF', darkBg: '#2B171A', border: '#E45959', order: 5 },
    { key: 'high', label: 'High', short: 'HIGH', bg: '#E97659', text: '#FFFFFF', darkBg: '#2B1D19', border: '#E97659', order: 4 },
    { key: 'average', label: 'Average', short: 'AVG', bg: '#FFA059', text: '#1E1400', darkBg: '#2C2014', border: '#FFA059', order: 3 },
    { key: 'warning', label: 'Warning', short: 'WARN', bg: '#FFC859', text: '#1E1900', darkBg: '#2B2513', border: '#FFC859', order: 2 },
    { key: 'information', label: 'Information', short: 'INFO', bg: '#7499FF', text: '#FFFFFF', darkBg: '#181F33', border: '#7499FF', order: 1 },
    { key: 'not_classified', label: 'Not classified', short: 'N/C', bg: '#97AAB3', text: '#111827', darkBg: '#1F2327', border: '#97AAB3', order: 0 }
];

const SEV_MAP = ZABBIX_SEVERITIES.reduce((acc, s) => ({ ...acc, [s.key]: s }), {});

const VENDOR_BADGES = {
    'Microsoft Defender': { code: 'MDE', icon: Shield, color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/20' },
    'FortiGate': { code: 'FG', icon: Globe, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
    'ThreatDown': { code: 'TD', icon: ShieldCheck, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' }
};

// ═══════════════════════════════════════════════════════════════════════
// Utilities
// ═══════════════════════════════════════════════════════════════════════

function formatDuration(seconds) {
    if (!seconds || seconds <= 0) return '< 1m';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

function formatZabbixTime(dateStr) {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '—';
        const pad = n => String(n).padStart(2, '0');
        const Y = d.getFullYear();
        const M = pad(d.getMonth() + 1);
        const D = pad(d.getDate());
        const h = pad(d.getHours());
        const m = pad(d.getMinutes());
        const s = pad(d.getSeconds());
        return `${Y}-${M}-${D} ${h}:${m}:${s}`;
    } catch {
        return '—';
    }
}

function timeAgo(dateStr) {
    if (!dateStr) return '—';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

// ═══════════════════════════════════════════════════════════════════════
// Zabbix Sub-Components
// ═══════════════════════════════════════════════════════════════════════

/**
 * The Iconic Zabbix Severity Bar Matrix
 */
const ZabbixSeverityBar = ({ counts = {}, activeFilter, onSelect }) => {
    return (
        <div className="grid grid-cols-6 rounded-md overflow-hidden border border-[#2b3240] shadow-md">
            {ZABBIX_SEVERITIES.map(s => {
                const count = counts[s.key] || 0;
                const isSelected = activeFilter === s.key;
                const isDisaster = s.key === 'disaster' && count > 0;

                return (
                    <button
                        key={s.key}
                        onClick={() => onSelect(isSelected ? null : s.key)}
                        style={{
                            backgroundColor: isSelected ? s.bg : s.darkBg,
                            borderColor: s.border,
                            color: isSelected ? s.text : (count > 0 ? '#FFFFFF' : '#6b7280')
                        }}
                        className={`px-3 py-2.5 flex flex-col items-center justify-center transition-all relative border-r border-[#2b3240] last:border-r-0 hover:brightness-125 select-none ${
                            isSelected ? 'ring-2 ring-white/60 font-bold z-10' : ''
                        }`}
                        title={`Filter by ${s.label}`}
                    >
                        {isDisaster && (
                            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 animate-ping" />
                        )}
                        <span className="text-[10px] uppercase font-bold tracking-wider opacity-90">{s.label}</span>
                        <span className={`text-xl font-mono font-bold tracking-tight mt-0.5 ${count > 0 ? 'text-white' : 'text-slate-600'}`}>
                            {count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

/**
 * Zabbix Collapsible Filter Box
 */
const ZabbixFilterBox = ({
    hostGroups,
    selectedGroup,
    onSelectGroup,
    searchQuery,
    onSearchChange,
    selectedSeverity,
    onSelectSeverity,
    sourceFilter,
    onSelectSource,
    ackFilter,
    onSelectAck,
    onReset
}) => {
    const [isOpen, setIsOpen] = useState(true);

    return (
        <div className="bg-[#181b23] border border-[#2b3240] rounded-md text-xs shadow-sm">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-4 py-2 bg-[#1d212b] border-b border-[#2b3240] flex items-center justify-between text-slate-300 hover:text-white font-medium transition-colors"
            >
                <div className="flex items-center gap-2">
                    <Filter className="w-3.5 h-3.5 text-blue-400" />
                    <span className="uppercase tracking-wider font-bold text-[11px] text-slate-300">Filter</span>
                    {(selectedGroup || searchQuery || selectedSeverity || sourceFilter || ackFilter !== 'all') && (
                        <span className="bg-blue-500/20 text-blue-400 border border-blue-500/30 text-[9px] px-1.5 py-0.2 rounded font-bold">
                            Active Filters
                        </span>
                    )}
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden p-4 space-y-4"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {/* Host Groups */}
                            <div>
                                <label className="block text-[11px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Host Groups</label>
                                <select
                                    value={selectedGroup || ''}
                                    onChange={e => onSelectGroup(e.target.value || null)}
                                    className="w-full bg-[#12151c] border border-[#2b3240] rounded px-2.5 py-1.5 text-slate-200 focus:outline-none focus:border-blue-500 text-xs"
                                >
                                    <option value="">All Groups</option>
                                    {hostGroups.map(g => (
                                        <option key={g.name} value={g.name}>{g.name} ({g.hosts.length})</option>
                                    ))}
                                </select>
                            </div>

                            {/* Host / Problem Search */}
                            <div>
                                <label className="block text-[11px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Problem or Host</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={e => onSearchChange(e.target.value)}
                                        placeholder="e.g. Oakville, SSL, update..."
                                        className="w-full bg-[#12151c] border border-[#2b3240] rounded pl-8 pr-3 py-1.5 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500 text-xs"
                                    />
                                    <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2" />
                                </div>
                            </div>

                            {/* Source System */}
                            <div>
                                <label className="block text-[11px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Source Telemetry</label>
                                <select
                                    value={sourceFilter || ''}
                                    onChange={e => onSelectSource(e.target.value || null)}
                                    className="w-full bg-[#12151c] border border-[#2b3240] rounded px-2.5 py-1.5 text-slate-200 focus:outline-none focus:border-blue-500 text-xs"
                                >
                                    <option value="">All Sources</option>
                                    <option value="FortiGate">FortiGate Syslog</option>
                                    <option value="Microsoft Defender">Microsoft Defender</option>
                                    <option value="ThreatDown">ThreatDown / Malwarebytes</option>
                                </select>
                            </div>

                            {/* Acknowledged Status */}
                            <div>
                                <label className="block text-[11px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Problem Status</label>
                                <div className="flex rounded border border-[#2b3240] overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => onSelectAck('all')}
                                        className={`flex-1 py-1.5 text-center text-[11px] font-medium transition-colors ${ackFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-[#12151c] text-slate-400 hover:text-white'}`}
                                    >
                                        All
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onSelectAck('unack')}
                                        className={`flex-1 py-1.5 text-center text-[11px] font-medium transition-colors border-l border-r border-[#2b3240] ${ackFilter === 'unack' ? 'bg-red-600 text-white' : 'bg-[#12151c] text-slate-400 hover:text-white'}`}
                                    >
                                        Unack only
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onSelectAck('ack')}
                                        className={`flex-1 py-1.5 text-center text-[11px] font-medium transition-colors ${ackFilter === 'ack' ? 'bg-emerald-600 text-white' : 'bg-[#12151c] text-slate-400 hover:text-white'}`}
                                    >
                                        Ack only
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-[#2b3240]">
                            {/* Severity Checkbox Pills */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mr-1">Severity:</span>
                                {ZABBIX_SEVERITIES.map(s => {
                                    const isSel = selectedSeverity === s.key;
                                    return (
                                        <button
                                            key={s.key}
                                            type="button"
                                            onClick={() => onSelectSeverity(isSel ? null : s.key)}
                                            style={{
                                                backgroundColor: isSel ? s.bg : 'transparent',
                                                borderColor: s.border,
                                                color: isSel ? s.text : s.bg
                                            }}
                                            className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border transition-all ${
                                                isSel ? 'shadow-sm' : 'opacity-70 hover:opacity-100'
                                            }`}
                                        >
                                            {s.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Reset Button */}
                            <button
                                onClick={onReset}
                                className="px-3 py-1 bg-[#232733] border border-[#2b3240] hover:bg-[#2b3240] text-slate-300 rounded text-xs flex items-center gap-1.5 transition-colors"
                            >
                                <RotateCcw className="w-3 h-3 text-slate-400" />
                                Reset Filters
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

/**
 * Zabbix Problems Table
 */
const ZabbixProblemsTable = ({ problems, onOpenHost, onAcknowledge }) => {
    const [expandedRows, setExpandedRows] = useState({});

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    if (problems.length === 0) {
        return (
            <div className="bg-[#181b23] border border-[#2b3240] rounded-md p-12 text-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-500/40 mx-auto mb-2" />
                <h3 className="text-sm font-semibold text-slate-300">No problems found</h3>
                <p className="text-xs text-slate-500 mt-1">All monitored hosts are operating within nominal thresholds or match no active filter.</p>
            </div>
        );
    }

    return (
        <div className="bg-[#181b23] border border-[#2b3240] rounded-md overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                    <thead>
                        <tr className="bg-[#1d212b] border-b border-[#2b3240] text-[11px] text-slate-400 uppercase font-semibold tracking-wider">
                            <th className="py-2.5 px-3 w-10 text-center">#</th>
                            <th className="py-2.5 px-3 w-36">Time</th>
                            <th className="py-2.5 px-3 w-28 text-center">Severity</th>
                            <th className="py-2.5 px-3 w-24 text-center">Status</th>
                            <th className="py-2.5 px-3 w-48">Host</th>
                            <th className="py-2.5 px-3">Problem</th>
                            <th className="py-2.5 px-3 w-28">Duration</th>
                            <th className="py-2.5 px-3 w-20 text-center">Ack</th>
                            <th className="py-2.5 px-3 w-24">Source</th>
                            <th className="py-2.5 px-3 w-16 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#242936]">
                        {problems.map((p, idx) => {
                            const sev = SEV_MAP[p.severity] || SEV_MAP.information;
                            const isExpanded = !!expandedRows[p.id];
                            const isDisaster = p.severity === 'disaster';
                            const vendor = VENDOR_BADGES[p.source] || { code: 'SYS', color: 'text-slate-400', bg: 'bg-slate-500/10' };

                            return (
                                <React.Fragment key={p.id || idx}>
                                    <tr className={`hover:bg-[#1f2430] transition-colors ${isExpanded ? 'bg-[#1b202c]' : ''} ${isDisaster && !p.acknowledged ? 'bg-red-500/5' : ''}`}>
                                        <td className="py-2 px-3 text-slate-600 font-mono text-[11px] text-center">
                                            {idx + 1}
                                        </td>
                                        <td className="py-2 px-3 font-mono text-[11px] text-slate-400 whitespace-nowrap">
                                            <span title={timeAgo(p.time)}>{formatZabbixTime(p.time)}</span>
                                        </td>
                                        <td className="py-1 px-3 text-center">
                                            <span
                                                style={{ backgroundColor: sev.bg, color: sev.text }}
                                                className={`inline-block px-2.5 py-0.5 rounded-sm text-[10px] font-extrabold uppercase tracking-wider shadow-sm select-none ${
                                                    isDisaster && !p.acknowledged ? 'animate-pulse' : ''
                                                }`}
                                            >
                                                {sev.label}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 text-center">
                                            {p.status === 'RESOLVED' ? (
                                                <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase">
                                                    RESOLVED
                                                </span>
                                            ) : (
                                                <span className="bg-red-500/20 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase">
                                                    PROBLEM
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 px-3">
                                            <button
                                                onClick={() => onOpenHost(p.hostId, p.host)}
                                                className="text-[#4b91f7] hover:underline font-medium hover:text-blue-300 text-left truncate max-w-[180px] block"
                                                title={`View Host: ${p.host}`}
                                            >
                                                {p.host}
                                            </button>
                                        </td>
                                        <td className="py-2 px-3">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => toggleRow(p.id)}
                                                    className="font-medium text-slate-200 hover:text-white text-left transition-colors flex-1 line-clamp-1"
                                                >
                                                    {p.problem}
                                                </button>
                                                {p.count > 1 && (
                                                    <span className="bg-white/10 text-slate-400 text-[10px] font-mono px-1.5 py-0.5 rounded font-bold flex-shrink-0">
                                                        {p.count}x
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 font-mono text-[11px] text-slate-400 whitespace-nowrap">
                                            {formatDuration(p.duration)}
                                        </td>
                                        <td className="py-2 px-3 text-center">
                                            {p.acknowledged ? (
                                                <button
                                                    onClick={() => onAcknowledge(p)}
                                                    className="text-emerald-400 hover:underline font-bold text-[11px] inline-flex items-center gap-1"
                                                    title={`Ack by ${p.ackUser || 'Analyst'}: ${p.ackMessage || 'No message'}`}
                                                >
                                                    <Check className="w-3 h-3" />
                                                    Yes
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => onAcknowledge(p)}
                                                    className="text-[#e45959] hover:underline font-bold text-[11px] bg-red-500/10 hover:bg-red-500/20 px-2 py-0.5 rounded border border-red-500/30 transition-all"
                                                >
                                                    No
                                                </button>
                                            )}
                                        </td>
                                        <td className="py-2 px-3">
                                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${vendor.bg} ${vendor.color}`}>
                                                {vendor.code}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 text-center">
                                            <button
                                                onClick={() => toggleRow(p.id)}
                                                className="p-1 hover:bg-white/10 rounded text-slate-400 hover:text-white transition-colors"
                                                title="Toggle Details"
                                            >
                                                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                            </button>
                                        </td>
                                    </tr>

                                    {/* Expanded Operational Data & Tags */}
                                    <AnimatePresence>
                                        {isExpanded && (
                                            <tr>
                                                <td colSpan={10} className="p-0 bg-[#12151c] border-b border-[#2b3240]">
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        className="p-4 space-y-3"
                                                    >
                                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                                                            <div className="bg-[#181b23] p-2.5 rounded border border-[#2b3240]">
                                                                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Item / Trigger</span>
                                                                <span className="font-mono text-slate-300">{p.category || 'System Trigger'}</span>
                                                            </div>
                                                            <div className="bg-[#181b23] p-2.5 rounded border border-[#2b3240]">
                                                                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Telemetry Origin</span>
                                                                <span className="text-slate-300 font-semibold">{p.source}</span>
                                                            </div>
                                                            <div className="bg-[#181b23] p-2.5 rounded border border-[#2b3240]">
                                                                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Initial Event Time</span>
                                                                <span className="font-mono text-slate-300">{formatZabbixTime(p.firstOccurrence || p.time)}</span>
                                                            </div>
                                                            <div className="bg-[#181b23] p-2.5 rounded border border-[#2b3240]">
                                                                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Event Multiplier</span>
                                                                <span className="font-mono text-slate-300">{p.count || 1} occurrences</span>
                                                            </div>
                                                        </div>

                                                        {p.details && (
                                                            <div className="bg-[#181b23] p-3 rounded border border-[#2b3240]">
                                                                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1.5">Operational Data & Tags</span>
                                                                <div className="flex flex-wrap gap-2 text-[11px]">
                                                                    {Object.entries(p.details)
                                                                        .filter(([, v]) => v !== undefined && v !== null && v !== '')
                                                                        .map(([k, v]) => (
                                                                            <span key={k} className="bg-[#242936] text-slate-300 border border-[#2e3547] px-2 py-0.5 rounded font-mono">
                                                                                <strong className="text-blue-400">{k}:</strong> {Array.isArray(v) ? v.join(', ') : String(v)}
                                                                            </span>
                                                                        ))}
                                                                    <span className="bg-[#242936] text-slate-300 border border-[#2e3547] px-2 py-0.5 rounded font-mono">
                                                                        <strong className="text-blue-400">host:</strong> {p.host}
                                                                    </span>
                                                                    <span className="bg-[#242936] text-slate-300 border border-[#2e3547] px-2 py-0.5 rounded font-mono">
                                                                        <strong className="text-blue-400">severity:</strong> {p.severity}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {p.acknowledged && p.ackMessage && (
                                                            <div className="bg-emerald-500/10 border border-emerald-500/20 p-2.5 rounded text-xs">
                                                                <span className="text-emerald-400 font-bold block text-[10px] uppercase mb-0.5">
                                                                    Acknowledged by {p.ackUser} ({timeAgo(p.ackTime)}):
                                                                </span>
                                                                <p className="text-slate-300 italic">{p.ackMessage}</p>
                                                            </div>
                                                        )}
                                                    </motion.div>
                                                </td>
                                            </tr>
                                        )}
                                    </AnimatePresence>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-2 bg-[#1d212b] border-t border-[#2b3240] text-[11px] text-slate-500 flex items-center justify-between font-mono">
                <span>Displaying {problems.length} problems</span>
                <span>Zabbix Standard Protocol • Auto-refresh active</span>
            </div>
        </div>
    );
};

/**
 * Zabbix Hosts Table (Monitoring → Hosts)
 */
const ZabbixHostsTable = ({ hosts, onOpenHost }) => {
    const [search, setSearch] = useState("");
    const [groupFilter, setGroupFilter] = useState("");

    const filtered = useMemo(() => {
        return hosts.filter(h => {
            if (groupFilter && h.group !== groupFilter) return false;
            if (search) {
                const q = search.toLowerCase();
                return (h.name || '').toLowerCase().includes(q) || (h.ip || '').toLowerCase().includes(q);
            }
            return true;
        });
    }, [hosts, search, groupFilter]);

    const groups = useMemo(() => Array.from(new Set(hosts.map(h => h.group))), [hosts]);

    return (
        <div className="space-y-3">
            {/* Quick Host Search & Group Filter */}
            <div className="flex items-center justify-between gap-4 flex-wrap bg-[#181b23] p-3 rounded border border-[#2b3240]">
                <div className="flex items-center gap-3">
                    <div className="relative w-64">
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search host or interface IP..."
                            className="w-full bg-[#12151c] border border-[#2b3240] rounded pl-8 pr-3 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
                        />
                        <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2" />
                    </div>

                    <select
                        value={groupFilter}
                        onChange={e => setGroupFilter(e.target.value)}
                        className="bg-[#12151c] border border-[#2b3240] rounded px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Groups</option>
                        {groups.map(g => (
                            <option key={g} value={g}>{g}</option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="font-mono">Total Monitored: <strong className="text-white">{filtered.length}</strong> hosts</span>
                </div>
            </div>

            {/* Hosts Table */}
            <div className="bg-[#181b23] border border-[#2b3240] rounded-md overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                        <thead>
                            <tr className="bg-[#1d212b] border-b border-[#2b3240] text-[11px] text-slate-400 uppercase font-semibold tracking-wider">
                                <th className="py-2.5 px-3">Name</th>
                                <th className="py-2.5 px-3">Interface</th>
                                <th className="py-2.5 px-3 text-center">Availability</th>
                                <th className="py-2.5 px-3 text-center">Status</th>
                                <th className="py-2.5 px-3 text-center">Latest Problems</th>
                                <th className="py-2.5 px-3">Host Group</th>
                                <th className="py-2.5 px-3">OS / Platform</th>
                                <th className="py-2.5 px-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#242936]">
                            {filtered.map(h => {
                                const isProblem = h.health !== 'ok';
                                const isDefender = h.vendor === 'Microsoft Defender';
                                const isFortiGate = h.vendor === 'FortiGate';
                                const isThreatDown = h.vendor === 'ThreatDown';

                                return (
                                    <tr key={h.id} className="hover:bg-[#1f2430] transition-colors">
                                        <td className="py-2 px-3">
                                            <button
                                                onClick={() => onOpenHost(h.id, h.displayName || h.name)}
                                                className="text-[#4b91f7] hover:underline font-semibold text-left flex items-center gap-2"
                                            >
                                                <span className={`w-2 h-2 rounded-full ${isProblem ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
                                                {h.displayName || h.name}
                                            </button>
                                        </td>
                                        <td className="py-2 px-3 font-mono text-[11px] text-slate-400">
                                            {h.ip && h.ip !== '—' ? `${h.ip}:${isFortiGate ? '514' : '10050'}` : 'Cloud Agent'}
                                        </td>
                                        <td className="py-2 px-3 text-center">
                                            {/* Zabbix Iconic Availability Badges */}
                                            <div className="inline-flex items-center gap-1 font-mono text-[9px] font-bold">
                                                <span className={`px-1.5 py-0.5 rounded ${isDefender ? 'bg-emerald-500 text-black' : 'bg-[#242936] text-slate-600'}`}>
                                                    ZBX
                                                </span>
                                                <span className={`px-1.5 py-0.5 rounded ${isFortiGate ? 'bg-emerald-500 text-black' : 'bg-[#242936] text-slate-600'}`}>
                                                    SNMP
                                                </span>
                                                <span className={`px-1.5 py-0.5 rounded ${isThreatDown ? 'bg-emerald-500 text-black' : 'bg-[#242936] text-slate-600'}`}>
                                                    EDR
                                                </span>
                                                <span className="px-1.5 py-0.5 rounded bg-emerald-500 text-black">
                                                    API
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 text-center">
                                            <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                                                Enabled
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 text-center font-mono">
                                            {h.problemCount > 0 ? (
                                                <span className="bg-red-500 text-white font-bold text-[10px] px-2 py-0.5 rounded-sm">
                                                    {h.problemCount}
                                                </span>
                                            ) : (
                                                <span className="text-slate-600 text-[11px]">0</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-3 text-slate-300">
                                            <span className="bg-[#242936] border border-[#2e3547] text-slate-300 px-2 py-0.5 rounded text-[10px] font-medium">
                                                {h.group}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 text-slate-400 max-w-[150px] truncate text-[11px]">
                                            {h.os || '—'}
                                        </td>
                                        <td className="py-2 px-3 text-right">
                                            <button
                                                onClick={() => onOpenHost(h.id, h.displayName || h.name)}
                                                className="px-2 py-1 bg-[#242936] hover:bg-blue-600 hover:text-white text-slate-300 rounded text-[10px] font-medium transition-colors"
                                            >
                                                Items &amp; Triggers
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

/**
 * Zabbix Classic Dashboard View (Monitoring → Dashboard)
 */
const ZabbixDashboardView = ({ kpis, hostGroups, problems, onSelectSev }) => {
    return (
        <div className="space-y-4">
            {/* Top row: System Info & 2D Matrix */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* System Information Widget */}
                <div className="lg:col-span-5 bg-[#181b23] border border-[#2b3240] rounded-md overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 bg-[#1d212b] border-b border-[#2b3240] flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">System Information</span>
                        <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-1.5 py-0.2 rounded font-bold">RUNNING</span>
                    </div>
                    <table className="w-full text-xs text-left border-collapse">
                        <tbody className="divide-y divide-[#242936]">
                            <tr>
                                <td className="py-2 px-4 text-slate-500 font-medium">Zabbix Server Version</td>
                                <td className="py-2 px-4 text-slate-200 font-mono">QueryTel Sentinel Core v7.0.4</td>
                            </tr>
                            <tr>
                                <td className="py-2 px-4 text-slate-500 font-medium">Zabbix Server Status</td>
                                <td className="py-2 px-4 text-emerald-400 font-semibold flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                    Online • Pollers Active
                                </td>
                            </tr>
                            <tr>
                                <td className="py-2 px-4 text-slate-500 font-medium">Number of Hosts (Monitored / Problem)</td>
                                <td className="py-2 px-4 font-mono text-slate-200">
                                    <strong>{kpis.totalHosts}</strong> / <span className="text-red-400 font-bold">{kpis.hostsWithProblems}</span>
                                </td>
                            </tr>
                            <tr>
                                <td className="py-2 px-4 text-slate-500 font-medium">Active Triggers / Problems</td>
                                <td className="py-2 px-4 font-mono text-amber-400 font-bold">
                                    {kpis.totalProblems}
                                </td>
                            </tr>
                            <tr>
                                <td className="py-2 px-4 text-slate-500 font-medium">Availability Index</td>
                                <td className="py-2 px-4 font-mono font-bold text-emerald-400">
                                    {kpis.availability}%
                                </td>
                            </tr>
                            <tr>
                                <td className="py-2 px-4 text-slate-500 font-medium">Required Server Performance (vps)</td>
                                <td className="py-2 px-4 text-slate-300 font-mono">148.2 new values/sec</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Problems by Severity 2D Matrix */}
                <div className="lg:col-span-7 bg-[#181b23] border border-[#2b3240] rounded-md overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 bg-[#1d212b] border-b border-[#2b3240] flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">Problems by Severity Matrix</span>
                        <span className="text-[10px] text-slate-500 font-mono">Host Group Breakdown</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs text-left border-collapse">
                            <thead>
                                <tr className="bg-[#151820] text-[10px] text-slate-400 uppercase font-semibold border-b border-[#2b3240]">
                                    <th className="py-2 px-3">Host Group</th>
                                    <th className="py-2 px-2 text-center text-red-400">Disaster</th>
                                    <th className="py-2 px-2 text-center text-orange-400">High</th>
                                    <th className="py-2 px-2 text-center text-amber-400">Average</th>
                                    <th className="py-2 px-2 text-center text-yellow-400">Warning</th>
                                    <th className="py-2 px-2 text-center text-blue-400">Info</th>
                                    <th className="py-2 px-3 text-center font-bold text-white">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#242936]">
                                {hostGroups.map(g => {
                                    const groupProblems = problems.filter(p => {
                                        const h = g.hosts.find(x => x.id === p.hostId);
                                        return !!h;
                                    });

                                    const dCount = groupProblems.filter(p => p.severity === 'disaster').length;
                                    const hCount = groupProblems.filter(p => p.severity === 'high').length;
                                    const aCount = groupProblems.filter(p => p.severity === 'average').length;
                                    const wCount = groupProblems.filter(p => p.severity === 'warning').length;
                                    const iCount = groupProblems.filter(p => p.severity === 'information').length;

                                    return (
                                        <tr key={g.name} className="hover:bg-[#1f2430]">
                                            <td className="py-2 px-3 font-medium text-slate-200">{g.name}</td>
                                            <td className="py-2 px-2 text-center font-mono">
                                                {dCount > 0 ? (
                                                    <span className="bg-[#e45959] text-white font-bold px-2 py-0.5 rounded-sm">
                                                        {dCount}
                                                    </span>
                                                ) : <span className="text-slate-600">0</span>}
                                            </td>
                                            <td className="py-2 px-2 text-center font-mono">
                                                {hCount > 0 ? (
                                                    <span className="bg-[#e97659] text-white font-bold px-2 py-0.5 rounded-sm">
                                                        {hCount}
                                                    </span>
                                                ) : <span className="text-slate-600">0</span>}
                                            </td>
                                            <td className="py-2 px-2 text-center font-mono">
                                                {aCount > 0 ? (
                                                    <span className="bg-[#ffa059] text-black font-bold px-2 py-0.5 rounded-sm">
                                                        {aCount}
                                                    </span>
                                                ) : <span className="text-slate-600">0</span>}
                                            </td>
                                            <td className="py-2 px-2 text-center font-mono">
                                                {wCount > 0 ? (
                                                    <span className="bg-[#ffc859] text-black font-bold px-2 py-0.5 rounded-sm">
                                                        {wCount}
                                                    </span>
                                                ) : <span className="text-slate-600">0</span>}
                                            </td>
                                            <td className="py-2 px-2 text-center font-mono">
                                                {iCount > 0 ? (
                                                    <span className="bg-[#7499ff] text-white font-bold px-2 py-0.5 rounded-sm">
                                                        {iCount}
                                                    </span>
                                                ) : <span className="text-slate-600">0</span>}
                                            </td>
                                            <td className="py-2 px-3 text-center font-mono font-bold text-white">
                                                {g.problems}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Bottom Row: Top 5 Disaster Triggers */}
            <div className="bg-[#181b23] border border-[#2b3240] rounded-md overflow-hidden shadow-sm">
                <div className="px-4 py-2.5 bg-[#1d212b] border-b border-[#2b3240] flex items-center justify-between">
                    <span className="text-xs font-bold text-red-400 uppercase tracking-wider flex items-center gap-2">
                        <AlertOctagon className="w-4 h-4 text-red-500" />
                        Disaster &amp; High Triggers
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">Top Priority</span>
                </div>
                <div className="divide-y divide-[#242936]">
                    {problems.filter(p => p.severity === 'disaster' || p.severity === 'high').slice(0, 6).map(p => (
                        <div key={p.id} className="p-3 flex items-center justify-between hover:bg-[#1f2430] text-xs">
                            <div className="flex items-center gap-3">
                                <span
                                    style={{ backgroundColor: SEV_MAP[p.severity]?.bg, color: SEV_MAP[p.severity]?.text }}
                                    className="px-2 py-0.5 rounded-sm text-[9px] font-extrabold uppercase tracking-wider font-mono"
                                >
                                    {p.severity}
                                </span>
                                <div>
                                    <span className="font-semibold text-slate-200">{p.host}: </span>
                                    <span className="text-slate-300">{p.problem}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-slate-400 font-mono text-[11px]">
                                <span>{formatDuration(p.duration)}</span>
                                <span className="bg-[#242936] text-slate-300 px-2 py-0.5 rounded text-[10px]">{p.source}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

/**
 * Zabbix Latest Data View (Monitoring → Latest Data)
 */
const ZabbixLatestDataView = ({ hosts }) => {
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        return hosts.filter(h => {
            if (!search) return true;
            return (h.name || '').toLowerCase().includes(search.toLowerCase());
        });
    }, [hosts, search]);

    return (
        <div className="space-y-3">
            <div className="bg-[#181b23] p-3 rounded border border-[#2b3240] flex items-center justify-between">
                <div className="relative w-72">
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Filter by host..."
                        className="w-full bg-[#12151c] border border-[#2b3240] rounded pl-8 pr-3 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
                    />
                    <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2" />
                </div>
                <span className="text-xs text-slate-400 font-mono">Showing items for {filtered.length} hosts</span>
            </div>

            <div className="bg-[#181b23] border border-[#2b3240] rounded-md overflow-hidden shadow-sm">
                <table className="w-full text-left border-collapse text-xs">
                    <thead>
                        <tr className="bg-[#1d212b] border-b border-[#2b3240] text-[11px] text-slate-400 uppercase font-semibold">
                            <th className="py-2.5 px-3">Host / Item Name</th>
                            <th className="py-2.5 px-3 w-40">Last Check</th>
                            <th className="py-2.5 px-3 w-40">Last Value</th>
                            <th className="py-2.5 px-3 w-28 text-center">Change</th>
                            <th className="py-2.5 px-3 w-28 text-center">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#242936]">
                        {filtered.slice(0, 20).map(h => (
                            <React.Fragment key={h.id}>
                                <tr className="bg-[#151820]/80">
                                    <td colSpan={5} className="py-2 px-3 font-bold text-slate-300 flex items-center gap-2">
                                        <Server className="w-3.5 h-3.5 text-blue-400" />
                                        {h.displayName || h.name}
                                        <span className="text-[10px] text-slate-500 font-normal font-mono">({h.ip || 'Cloud API'})</span>
                                    </td>
                                </tr>
                                <tr className="hover:bg-[#1f2430]">
                                    <td className="py-2 px-6 text-slate-400 font-mono">↳ Agent / Interface Status</td>
                                    <td className="py-2 px-3 font-mono text-slate-400">{timeAgo(h.lastSeen)}</td>
                                    <td className="py-2 px-3 font-mono text-emerald-400 font-bold">1 (Connected)</td>
                                    <td className="py-2 px-3 text-center text-slate-600 font-mono">+0</td>
                                    <td className="py-2 px-3 text-center">
                                        <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-2 py-0.5 rounded font-bold uppercase">OK</span>
                                    </td>
                                </tr>
                                <tr className="hover:bg-[#1f2430]">
                                    <td className="py-2 px-6 text-slate-400 font-mono">↳ Active Problems / Trigger State</td>
                                    <td className="py-2 px-3 font-mono text-slate-400">{timeAgo(h.lastSeen)}</td>
                                    <td className="py-2 px-3 font-mono text-slate-200">{h.problemCount || 0} active</td>
                                    <td className="py-2 px-3 text-center text-slate-600 font-mono">—</td>
                                    <td className="py-2 px-3 text-center">
                                        {h.problemCount > 0 ? (
                                            <span className="bg-red-500/20 text-red-400 text-[10px] px-2 py-0.5 rounded font-bold uppercase">PROBLEM</span>
                                        ) : (
                                            <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-2 py-0.5 rounded font-bold uppercase">OK</span>
                                        )}
                                    </td>
                                </tr>
                                {h.vendor === 'FortiGate' && (
                                    <tr className="hover:bg-[#1f2430]">
                                        <td className="py-2 px-6 text-slate-400 font-mono">↳ Denied / Blocked Session Rate</td>
                                        <td className="py-2 px-3 font-mono text-slate-400">{timeAgo(h.lastSeen)}</td>
                                        <td className="py-2 px-3 font-mono text-amber-400">{h.deniedSessions || 0} blocks</td>
                                        <td className="py-2 px-3 text-center text-slate-600 font-mono">+12/m</td>
                                        <td className="py-2 px-3 text-center">
                                            <span className="bg-blue-500/20 text-blue-400 text-[10px] px-2 py-0.5 rounded font-bold uppercase">MONITORED</span>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/**
 * Zabbix Problem Acknowledgment Modal
 */
const ZabbixAckModal = ({ problem, onClose, onSuccess }) => {
    const [message, setMessage] = useState("");
    const [closeProblem, setCloseProblem] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    if (!problem) return null;

    const sev = SEV_MAP[problem.severity] || SEV_MAP.information;

    const handleAcknowledge = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${backendURL}/api/soc/monitoring/acknowledge`, {
                problemId: problem.id,
                user: "SOC Lead Analyst",
                message: message || "Acknowledged via Zabbix console",
                closeProblem
            });
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Ack error:", err);
            alert("Failed to acknowledge: " + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#181b23] border border-[#2b3240] rounded-md w-full max-w-lg overflow-hidden shadow-2xl"
            >
                <div className="px-4 py-3 bg-[#1d212b] border-b border-[#2b3240] flex items-center justify-between">
                    <span className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-blue-400" />
                        Update Problem
                    </span>
                    <button onClick={onClose} className="text-slate-400 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <form onSubmit={handleAcknowledge} className="p-4 space-y-4 text-xs">
                    <div className="p-3 bg-[#12151c] rounded border border-[#2b3240] space-y-2">
                        <div className="flex items-center gap-2">
                            <span
                                style={{ backgroundColor: sev.bg, color: sev.text }}
                                className="px-2 py-0.5 rounded-sm font-extrabold uppercase text-[10px]"
                            >
                                {sev.label}
                            </span>
                            <span className="font-bold text-slate-200">{problem.host}</span>
                        </div>
                        <p className="text-slate-300">{problem.problem}</p>
                        <div className="text-[10px] text-slate-500 font-mono">
                            Event Time: {formatZabbixTime(problem.time)} • Duration: {formatDuration(problem.duration)}
                        </div>
                    </div>

                    <div>
                        <label className="block text-slate-400 font-semibold mb-1.5 uppercase tracking-wider text-[10px]">
                            Message / Analyst Note
                        </label>
                        <textarea
                            rows={3}
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            placeholder="Add explanation, action taken, ticket reference..."
                            className="w-full bg-[#12151c] border border-[#2b3240] rounded p-2 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500 text-xs"
                        />
                    </div>

                    <div className="space-y-2 pt-1 border-t border-[#2b3240]">
                        <label className="flex items-center gap-2 text-slate-300 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={closeProblem}
                                onChange={e => setCloseProblem(e.target.checked)}
                                className="rounded border-slate-700 bg-slate-900 text-blue-500 focus:ring-0"
                            />
                            <span>Close problem manually (mark RESOLVED)</span>
                        </label>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#2b3240]">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1.5 bg-[#232733] hover:bg-[#2b3240] text-slate-300 rounded text-xs transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-semibold transition-colors disabled:opacity-50"
                        >
                            {submitting ? 'Updating...' : 'Update Problem'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

/**
 * Zabbix Host Summary Modal
 */
const ZabbixHostModal = ({ hostId, hostName, onClose, onFilterHost }) => {
    const [hostData, setHostData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!hostId) return;
        setLoading(true);
        axios.get(`${backendURL}/api/soc/monitoring/host/${encodeURIComponent(hostId)}`)
            .then(res => setHostData(res.data))
            .catch(err => console.error("Failed to load host detail:", err))
            .finally(() => setLoading(false));
    }, [hostId]);

    if (!hostId) return null;

    const host = hostData?.host;
    const problems = hostData?.problems || [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#181b23] border border-[#2b3240] rounded-md w-full max-w-2xl overflow-hidden shadow-2xl max-h-[85vh] flex flex-col"
            >
                <div className="px-4 py-3 bg-[#1d212b] border-b border-[#2b3240] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Server className="w-4 h-4 text-blue-400" />
                        <span className="text-xs font-bold text-white uppercase tracking-wider">
                            Host Details: {hostName}
                        </span>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto space-y-4 text-xs flex-1">
                    {loading ? (
                        <div className="py-12 text-center text-slate-500">
                            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-500" />
                            Loading host telemetry...
                        </div>
                    ) : host ? (
                        <>
                            {/* Host Info Table */}
                            <div className="bg-[#12151c] rounded border border-[#2b3240] overflow-hidden">
                                <table className="w-full text-left">
                                    <tbody className="divide-y divide-[#242936]">
                                        <tr>
                                            <td className="py-2 px-3 text-slate-500 font-semibold w-36">Host Group</td>
                                            <td className="py-2 px-3 text-slate-200">{host.group}</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3 text-slate-500 font-semibold">Interface / IP</td>
                                            <td className="py-2 px-3 font-mono text-blue-400">{host.ip || 'Cloud API'}</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3 text-slate-500 font-semibold">Vendor / Source</td>
                                            <td className="py-2 px-3 text-slate-200">{host.vendor} ({host.agentVersion || 'Syslog'})</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3 text-slate-500 font-semibold">Operating System</td>
                                            <td className="py-2 px-3 text-slate-200">{host.os || '—'}</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 px-3 text-slate-500 font-semibold">Status / Health</td>
                                            <td className="py-2 px-3">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${host.health === 'ok' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                                    {host.health}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Active Triggers for Host */}
                            <div>
                                <h4 className="font-bold text-slate-300 uppercase tracking-wider text-[11px] mb-2 flex items-center justify-between">
                                    <span>Associated Triggers &amp; Problems</span>
                                    <span className="text-slate-500 font-mono">{problems.length} total</span>
                                </h4>
                                {problems.length === 0 ? (
                                    <div className="p-4 bg-[#12151c] rounded border border-[#2b3240] text-center text-slate-500">
                                        No active problems on this host
                                    </div>
                                ) : (
                                    <div className="bg-[#12151c] rounded border border-[#2b3240] divide-y divide-[#242936]">
                                        {problems.map((p, i) => {
                                            const sev = SEV_MAP[p.severity] || SEV_MAP.information;
                                            return (
                                                <div key={i} className="p-2.5 flex items-center justify-between hover:bg-white/[0.02]">
                                                    <div className="flex items-center gap-2">
                                                        <span
                                                            style={{ backgroundColor: sev.bg, color: sev.text }}
                                                            className="px-2 py-0.5 rounded-sm font-extrabold uppercase text-[9px]"
                                                        >
                                                            {sev.label}
                                                        </span>
                                                        <span className="text-slate-200">{p.problem}</span>
                                                    </div>
                                                    <span className="font-mono text-slate-500 text-[10px]">{formatDuration(p.duration)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="py-8 text-center text-red-400">Host details not found</div>
                    )}
                </div>

                <div className="px-4 py-3 bg-[#1d212b] border-t border-[#2b3240] flex items-center justify-between">
                    <button
                        onClick={() => {
                            onFilterHost(hostName);
                            onClose();
                        }}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-semibold transition-colors"
                    >
                        Filter Problems for this Host
                    </button>
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 bg-[#232733] hover:bg-[#2b3240] text-slate-300 rounded text-xs transition-colors"
                    >
                        Close
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Main Page Component: Zabbix Unified Infrastructure Monitoring
// ═══════════════════════════════════════════════════════════════════════

export default function InfraMonitoring() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [refreshCountdown, setRefreshCountdown] = useState(30);
    const [autoRefresh, setAutoRefresh] = useState(true);

    // Active Zabbix View Tab: 'problems' | 'hosts' | 'dashboard' | 'latest'
    const [activeTab, setActiveTab] = useState('problems');

    // Filters
    const [severityFilter, setSeverityFilter] = useState(null);
    const [groupFilter, setGroupFilter] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sourceFilter, setSourceFilter] = useState(null);
    const [ackFilter, setAckFilter] = useState('all'); // 'all' | 'unack' | 'ack'

    // Modals
    const [ackProblemModal, setAckProblemModal] = useState(null);
    const [selectedHostModal, setSelectedHostModal] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            const res = await axios.get(`${backendURL}/api/soc/monitoring/overview`);
            setData(res.data);
            setLastRefresh(new Date());
            setRefreshCountdown(30);
            setError(null);
        } catch (err) {
            console.error("Monitoring fetch error:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Auto-refresh countdown (30s)
    useEffect(() => {
        if (!autoRefresh) return;
        const timer = setInterval(() => {
            setRefreshCountdown(prev => {
                if (prev <= 1) {
                    fetchData();
                    return 30;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [autoRefresh, fetchData]);

    // Filter problems list
    const filteredProblems = useMemo(() => {
        return (data?.problems || []).filter(p => {
            if (severityFilter && p.severity !== severityFilter) return false;
            if (groupFilter) {
                const hostInGroup = (data?.hosts || []).find(h => h.id === p.hostId && h.group === groupFilter);
                if (!hostInGroup) return false;
            }
            if (sourceFilter && p.source !== sourceFilter) return false;
            if (ackFilter === 'unack' && p.acknowledged) return false;
            if (ackFilter === 'ack' && !p.acknowledged) return false;
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                return (p.host || '').toLowerCase().includes(q) || (p.problem || '').toLowerCase().includes(q);
            }
            return true;
        });
    }, [data, severityFilter, groupFilter, sourceFilter, ackFilter, searchQuery]);

    const resetFilters = () => {
        setSeverityFilter(null);
        setGroupFilter(null);
        setSearchQuery('');
        setSourceFilter(null);
        setAckFilter('all');
    };

    if (loading && !data) {
        return (
            <div className="min-h-screen bg-[#11141a] flex items-center justify-center font-mono">
                <div className="text-center p-8 bg-[#181b23] border border-[#2b3240] rounded-md shadow-2xl">
                    <Monitor className="w-10 h-10 text-blue-500 mx-auto mb-3 animate-pulse" />
                    <p className="text-slate-300 text-sm font-semibold">Connecting to Sentinel Zabbix Poller...</p>
                    <p className="text-slate-600 text-xs mt-1">Aggregating telemetry from FortiGate, Defender, ThreatDown</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#11141a] text-slate-100 font-sans pb-12">
            {/* ─── Top Zabbix Header Bar ─── */}
            <div className="bg-[#181b23] border-b border-[#2b3240] px-6 py-2.5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded bg-red-600 flex items-center justify-center font-bold text-white text-xs font-mono shadow-sm">
                            Z
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-sm font-bold text-white tracking-wide">
                                    Monitoring / {activeTab.toUpperCase()}
                                </h1>
                                <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-mono text-[9px] font-bold px-1.5 py-0.2 rounded">
                                    ZABBIX 7.0 COMPATIBLE
                                </span>
                            </div>
                            <p className="text-[10px] text-slate-500 font-mono">
                                Unified Telemetry · FortiGate Syslog (514) · Microsoft Defender ATP · ThreatDown Cloud
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Auto-refresh indicator */}
                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs font-mono transition-all ${
                                autoRefresh
                                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                                    : 'bg-[#12151c] border-[#2b3240] text-slate-500'
                            }`}
                        >
                            {autoRefresh ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                            {autoRefresh ? `${refreshCountdown}s` : 'Paused'}
                        </button>

                        <button
                            onClick={fetchData}
                            className="flex items-center gap-1.5 px-3 py-1 bg-[#232733] border border-[#2b3240] hover:bg-[#2b3240] text-slate-300 rounded text-xs transition-colors"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>

                        {lastRefresh && (
                            <span className="text-[10px] text-slate-500 font-mono ml-1">
                                {timeAgo(lastRefresh.toISOString())}
                            </span>
                        )}
                    </div>
                </div>

                {/* ─── Zabbix Navigation Tabs ─── */}
                <div className="flex items-center gap-1 mt-3 border-t border-[#232733] pt-2">
                    <button
                        onClick={() => setActiveTab('problems')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-xs font-semibold transition-all border-b-2 ${
                            activeTab === 'problems'
                                ? 'text-white border-red-500 bg-[#212634]'
                                : 'text-slate-400 border-transparent hover:text-slate-200'
                        }`}
                    >
                        <ShieldAlert className="w-3.5 h-3.5 text-red-400" />
                        Problems ({filteredProblems.length})
                    </button>

                    <button
                        onClick={() => setActiveTab('hosts')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-xs font-semibold transition-all border-b-2 ${
                            activeTab === 'hosts'
                                ? 'text-white border-blue-500 bg-[#212634]'
                                : 'text-slate-400 border-transparent hover:text-slate-200'
                        }`}
                    >
                        <Server className="w-3.5 h-3.5 text-blue-400" />
                        Hosts ({data?.hosts?.length || 0})
                    </button>

                    <button
                        onClick={() => setActiveTab('dashboard')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-xs font-semibold transition-all border-b-2 ${
                            activeTab === 'dashboard'
                                ? 'text-white border-emerald-500 bg-[#212634]'
                                : 'text-slate-400 border-transparent hover:text-slate-200'
                        }`}
                    >
                        <LayoutDashboard className="w-3.5 h-3.5 text-emerald-400" />
                        Dashboard / System Overview
                    </button>

                    <button
                        onClick={() => setActiveTab('latest')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t text-xs font-semibold transition-all border-b-2 ${
                            activeTab === 'latest'
                                ? 'text-white border-purple-500 bg-[#212634]'
                                : 'text-slate-400 border-transparent hover:text-slate-200'
                        }`}
                    >
                        <Activity className="w-3.5 h-3.5 text-purple-400" />
                        Latest Data
                    </button>
                </div>
            </div>

            {/* ─── Main Content Body ─── */}
            <div className="px-6 py-4 space-y-4 max-w-[1600px] mx-auto">
                {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-xs text-red-300 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        {error}
                    </div>
                )}

                {/* ─── TAB 1: PROBLEMS VIEW ─── */}
                {activeTab === 'problems' && (
                    <>
                        {/* Zabbix Severity Matrix Bar */}
                        {data?.kpis?.severityCounts && (
                            <ZabbixSeverityBar
                                counts={data.kpis.severityCounts}
                                activeFilter={severityFilter}
                                onSelect={setSeverityFilter}
                            />
                        )}

                        {/* Collapsible Filter Accordion */}
                        <ZabbixFilterBox
                            hostGroups={data?.hostGroups || []}
                            selectedGroup={groupFilter}
                            onSelectGroup={setGroupFilter}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            selectedSeverity={severityFilter}
                            onSelectSeverity={setSeverityFilter}
                            sourceFilter={sourceFilter}
                            onSelectSource={setSourceFilter}
                            ackFilter={ackFilter}
                            onSelectAck={setAckFilter}
                            onReset={resetFilters}
                        />

                        {/* The Zabbix Problems Table */}
                        <ZabbixProblemsTable
                            problems={filteredProblems}
                            onOpenHost={(id, name) => setSelectedHostModal({ id, name })}
                            onAcknowledge={p => setAckProblemModal(p)}
                        />
                    </>
                )}

                {/* ─── TAB 2: HOSTS VIEW ─── */}
                {activeTab === 'hosts' && (
                    <ZabbixHostsTable
                        hosts={data?.hosts || []}
                        onOpenHost={(id, name) => setSelectedHostModal({ id, name })}
                    />
                )}

                {/* ─── TAB 3: DASHBOARD VIEW ─── */}
                {activeTab === 'dashboard' && (
                    <ZabbixDashboardView
                        kpis={data?.kpis || {}}
                        hostGroups={data?.hostGroups || []}
                        problems={data?.problems || []}
                        onSelectSev={setSeverityFilter}
                    />
                )}

                {/* ─── TAB 4: LATEST DATA VIEW ─── */}
                {activeTab === 'latest' && (
                    <ZabbixLatestDataView hosts={data?.hosts || []} />
                )}
            </div>

            {/* ─── Modals ─── */}
            {ackProblemModal && (
                <ZabbixAckModal
                    problem={ackProblemModal}
                    onClose={() => setAckProblemModal(null)}
                    onSuccess={fetchData}
                />
            )}

            {selectedHostModal && (
                <ZabbixHostModal
                    hostId={selectedHostModal.id}
                    hostName={selectedHostModal.name}
                    onClose={() => setSelectedHostModal(null)}
                    onFilterHost={(name) => {
                        setActiveTab('problems');
                        setSearchQuery(name);
                    }}
                />
            )}
        </div>
    );
}
